import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Flex, Form, GetProp, message, Select, Table, Upload, UploadProps } from 'antd';
import Title from 'antd/es/typography/Title';
import ButtonBlack from '../../../ui/buttons/ButtonBlack';
import ButtonGreen from '../../../ui/buttons/ButtonGreen';
import { Dayjs } from 'dayjs';
import urls from '../../../../api/urls';
import { api } from '../../../../api/apiProvider';
import { useParams } from 'react-router-dom';
import { BriefTaskKpiData, ModelType, Product, } from '../../../../api/types';
import IconUploadFile from '../../../ui/icons/IconUploadFile';
import IconComments from '../../../ui/icons/IconComments';
import Column from 'antd/lib/table/Column';
import useSWRMutation from 'swr/mutation';
import ButtonRed from '../../../ui/buttons/ButtonRed';
import KpiDataFile from '../KpiDataFile';
import { isEqualValues } from '../../../../utils';
import Roles from '../../../../roles/Roles';
import useOptions, { MediaFactorType } from './useOptions';
import useBriefTasks from './useBriefTasks';
import { DefaultOptionType } from 'antd/es/select';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

interface Item {
  id: number;
  kpi: string;
  briefKpiTaskData: BriefTaskKpiData[];
  period: Dayjs[];
  manager: string;
  commentary: string;
}

interface Model {
  id: string,
  '@id': string,
  name: string,
}

export interface BriefDataType {
  '@context': string,
  '@id': string,
  '@type': string,
  products: Product[],
  commentary: string,
  dataDateEnd: string,
  dataDateStart: string,
  dateEnd: string,
  id: string;
  manager?: string,
  name: string,
  product: Model,
  seasonalityText: string,
  tasks: string[],
  modelType: ModelType,
}

interface DataType extends Item {
  key: string;
  dataIndex: string;
}

interface MediaFactorsProps {
  brief: BriefDataType,
  setSelectedCommentaryId: Function,
  selectedCommentaryId: Number,
  type: MediaFactorType
}

const updateTask = async (id: number, data: any) => {
  return await api.patch(`${urls.briefTaskChannels}/${id}`, { arg: data });
};

const deleteTask = async (id: number) => {
  return await api.delete(`${urls.briefTaskChannels}/${id}`);
};

const addTaskData = async (data: any) => {
  return await api.post(urls.briefTaskChannelsData, { arg: data });
};

const updateTaskData = async (id: number, data: any) => {
  return await api.patch(`${urls.briefTaskChannelsData}/${id}`, { arg: data });
};

const uploadFile = async (formData: any) => {
  return await api.postFile(`${urls.mediaObjects}`, { arg: formData });
};

export default function MediaFactors ({
  brief,
  selectedCommentaryId, setSelectedCommentaryId, type
}: MediaFactorsProps) {
  const { t } = useTranslation();
  const routeParams = useParams();
  const briefId = Number(routeParams.id) || 0;
  const [form] = Form.useForm();
  const [selectedChannelsIds, setSelectedChannelsIds] = useState<number[]>([]);
  const [isDataSaving, setIsDataSaving] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<FormData[]>([]);
  const [isValuesChanged, setIsValuesChanged] = useState<boolean>(false);

  const { managersOptions, statusesOptions, channelsOptions, productsOptions, media } = useOptions(type, brief);
  const { tasks, fileList, setFileList, refreshBriefTasks } = useBriefTasks(type, brief);

  const isOnline = () => type === 'online';

  const {
    trigger: createTask,
  } = useSWRMutation(urls.briefTaskChannels, api.post);

  useEffect(() => {
    if (tasks) {
      form.setFieldValue('factors', tasks);
    }
  }, [tasks]);

  useEffect(() => {
    if (fileList) {
      setFileList(fileList);
    }
  }, [fileList]);

  const briefHasMultipleProducts = () => {
    return brief && brief.modelType && brief.modelType.isBriefManyProducts;
  };

  const onFinish = async (values: any) => {
    if (!isEditable()) {
      return;
    }

    setIsDataSaving(true);
    for (let index in values.factors) {
      const taskData = values.factors[index];
      let product = taskData.product;

      if (!briefHasMultipleProducts() && brief.products && brief.products[0]) {
        product = brief.products[0]['@id'] || '';
      } else if (briefHasMultipleProducts()) {
        product = taskData.product;
      }

      const data = {
        name: `Задача на сбор данных по каналу ${'channelName'}`,
        manager: taskData.manager,
        media: media?.['@id'],
        product: product,
        briefChannels: taskData.briefChannels,
        brief: `api${urls.briefs}/${briefId}`,
        dataDateStart: brief.dataDateStart,
        dataDateEnd: brief.dataDateEnd,
      };

      let mediaFactorId = taskData.id;

      if (mediaFactorId > 0) {
        await updateTask(mediaFactorId, data).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description']);
          }
        });
      } else {
        const response = await createTask(data).then(response => response).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description']);
          }
        });

        if (response && response.id) {
          mediaFactorId = response.id;
        }
      }

      if (!mediaFactorId) {
        setIsDataSaving(false);
        setIsValuesChanged(false);
        return;
      }

      let fileId = taskData.file && taskData.file['@id'] ? taskData.file['@id'] : null;

      if (taskData.file && taskData.file[0] && taskData.file[0].uid) {
        const formData = new FormData();

        formData.append('file', taskData.file[0] as FileType);

        const response = await uploadFile(formData).then(response => response).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description']);
          }
        });

        if (response && response['@id']) {
          fileId = response['@id'];
        }
      }

      if (taskData.taskDataId) {
        await updateTaskData(taskData.taskDataId, {
          status: taskData.status,
          file: taskData.file && taskData.file['@id']
            ? taskData.file['@id']
            : fileId,
        });
      } else {
        await addTaskData({
          file: fileId,
          task: `/api${urls.briefTaskChannels}/${mediaFactorId}`,
          status: taskData.status,
        }).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description']);
          }
        });
      }
    }

    message.success(t('common.saved'));
    setFileList([]);
    refreshBriefTasks();
    setIsDataSaving(false);
    setIsValuesChanged(false);
  };

  const deleteTasks = async () => {
    let deletedCount = 0;
    let factors = form.getFieldValue('factors');

    for (let index of selectedChannelsIds) {
      const record = factors[index];
      if (record && record.id) {
        await deleteTask(record.id).then(() => {
          deletedCount++;
        }).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description']);
          }

          return;
        });
      } else {
        factors.splice(index, 1);
      }
    }

    form.setFieldValue('factors', factors);

    if (deletedCount > 0) {
      message.success(t('common.deleted'));
      refreshBriefTasks();
    }

    setSelectedChannelsIds([]);
  };

  const getRowClassName = (index: number, record: any) => {
    if (selectedChannelsIds.includes(index)) {
      return 'selected-task-row';
    }

    const val = form.getFieldValue(['factors', index, 'status']);
    const status = statusesOptions && statusesOptions.find((item: DefaultOptionType) => {
      return item.value === val;
    });

    if (status && status.label === 'принято') {
      return 'accepted-task-row';
    }

    return '';
  };

  const onValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.factors) {
      form.setFieldsValue({ factors: allValues.factors });
    }

    if (allValues.length !== initialValues?.length) {
      setIsValuesChanged(true);
      return;
    }

    for (let key in initialValues) {
      if (!initialValues.hasOwnProperty(key)) {
        continue;
      }

      const initialValue = initialValues[Number(key)];
      if (key in allValues) {
        const changedValue = allValues[key];

        if (!isEqualValues(initialValue, changedValue)) {
          setIsValuesChanged(true);
          return;
        }
      }
    }

    setIsValuesChanged(false);
  };

  const isEditable = () => Roles.hasAccess('canCreateOrUpdateTaskChannels');

  return (
    <Flex vertical gap={'middle'}>
      <Title level={5}
             style={{ color: '#85929A' }}>{isOnline() ? t('managerTasks.online') : t('managerTasks.offline')}</Title>
      <Form
        form={form}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
      >
        <Form.List name="factors">
          {(factors, { add, remove }) => {
            return <Table
              bordered
              dataSource={factors}
              rowKey={'key'}
              scroll={{ x: 800 }}
              pagination={false}
              size={'middle'}
              footer={() => {
                return (
                  <Form.Item>
                    <Flex justify={'space-between'}>
                      <Flex gap={'small'}>
                        <ButtonGreen
                          onClick={add}
                          disabled={!isEditable()}
                        >{t('common.add')}</ButtonGreen>
                        {selectedChannelsIds && selectedChannelsIds.length > 0 &&
                          <ButtonRed
                            onClick={deleteTasks}
                            disabled={!isEditable()}
                          >{t('common.delete')}</ButtonRed>
                        }
                      </Flex>
                      <ButtonBlack
                        onClick={form.submit}
                        loading={isDataSaving}
                        disabled={!isValuesChanged || !isEditable()}
                      >{t('common.save')}</ButtonBlack>
                    </Flex>
                  </Form.Item>
                );
              }}
              rowClassName={(record, index) => getRowClassName(index, record)}
            >
              <Column
                dataIndex={'id'}
                title={'№'}
                render={
                  (value: any, record: DataType, index: number) => {
                    return <div>{index + 1}</div>;
                  }
                }
                width={50}
              />
              <Column
                dataIndex={'actions'}
                title={''}
                render={
                  (value: any, record: DataType, index: number) => {
                    return <Checkbox
                      checked={selectedChannelsIds.includes(index)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedChannelsIds([
                            ...selectedChannelsIds,
                            index,
                          ]);
                        } else {
                          setSelectedChannelsIds([...selectedChannelsIds.filter(function (value, valueIndex, arr) {
                            return value !== index;
                          })]);
                        }
                      }}
                      disabled={!isEditable()}
                    />;
                  }
                }
                width={30}
              />
              {briefHasMultipleProducts() &&
                <Column
                  dataIndex={'product'}
                  title={t('brief.product')}
                  render={
                    (value: any, record: DataType, index: number) => {
                      return (
                        <Form.Item name={[index, 'product']} rules={[{ required: true, message: t('common.choose') }]}>
                          <Select
                            value={value}
                            style={{ width: '100%' }}
                            options={productsOptions}
                          />
                        </Form.Item>);
                    }
                  }
                  width={400}
                />
              }
              <Column
                dataIndex={'briefChannels'}
                title={t('managerTasks.channels')}
                render={(value: any, record: DataType, index: number) => {
                  const factors = form.getFieldValue('factors') || [];
                  
                  const currentProduct = factors[index]?.product;
                  const isMultiProduct = briefHasMultipleProducts();

                  const usedChannels = new Set<string>();

                  factors.forEach((factor: any, i: number) => {
                    if (i === index || !Array.isArray(factor.briefChannels)) {
                      return;
                    }

                    if (
                      !isMultiProduct ||
                      factor.product === currentProduct
                    ) {
                      factor.briefChannels.forEach((channel: string) => {
                        usedChannels.add(channel);
                      });
                    }
                  });

                  const duplicateMessage = isMultiProduct
                    ? t('managerTasks.isSameProductChannels')
                    : t('managerTasks.isFactorWithSameChannel');

                  const options = channelsOptions.map((channel: any) => {
                    const disabled = usedChannels.has(channel.briefChannel);

                    return {
                      value: channel.briefChannel,
                      label: channel.label,
                      disabled,
                      title: disabled ? duplicateMessage : '',
                    };
                  });

                  return (
                    <Form.Item
                      name={[index, 'briefChannels']}
                      rules={[{ required: true, message: t('common.choose') }]}
                    >
                      <Select
                        value={value}
                        style={{ width: '100%' }}
                        options={options}
                        mode="multiple"
                        disabled={!isEditable()}
                        placeholder={t('common.choose')}
                        allowClear
                      />
                    </Form.Item>
                  );
                }}
                width={400}
              />
              <Column
                dataIndex={'manager'}
                title={t('managerTasks.responsible')}
                render={
                  (value: any, record: DataType, index: number) => {
                    return (
                      <Form.Item name={[index, 'manager']} rules={[{ required: true, message: t('common.choose') }]}>
                        <Select
                          value={value}
                          style={{ width: '100%' }}
                          options={managersOptions}
                          disabled={!isEditable()}
                        />
                      </Form.Item>);
                  }
                }
                width={400}
              />
              <Column
                dataIndex={'file'}
                title={t('managerTasks.dataSource')}
                render={
                  (value: any, record: DataType, index: number) => {
                    const file = form.getFieldValue(['factors', index, 'file']);

                    return <Flex vertical gap={'small'}
                                 id={'media-file-wrapper' + String(index) + type}
                    >
                      <Form.Item
                        shouldUpdate
                      >
                        {() => {

                          return (
                            <>
                              {file && file.id &&
                                <div key={file.id} style={{ height: '32px' }}>
                                  <KpiDataFile
                                    key={type + index}
                                    fileUrl={file.contentUrl}
                                    fileName={file.originalName}
                                    inputId={'media-file-wrapper' + String(index) + type}
                                    onRemove={() => {
                                      form.setFieldValue(['factors', index, 'file'], null);
                                      const newFileList = fileList;
                                      newFileList[index] = false;
                                      setFileList(newFileList);
                                      form.setFieldsValue({});
                                      setIsValuesChanged(true);
                                    }}
                                    editable={isEditable()}
                                  />
                                </div>
                              }
                              {(!file || fileList[index]) &&
                                <Upload
                                  id={'file' + index + '-' + type}
                                  beforeUpload={file => {
                                    form.setFieldValue(['factors', index, 'file'], [file]);
                                    const newFileList = fileList;
                                    newFileList[index] = true;
                                    setFileList(newFileList);
                                    form.setFieldsValue({});
                                    setIsValuesChanged(true);
                                    return false;
                                  }}
                                  maxCount={1}
                                  style={{ width: '100%' }}
                                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                  itemRender={(originNode, file) => {
                                    return fileList[index] ? <KpiDataFile
                                      key={type + index}
                                      fileUrl={file.url}
                                      fileName={file.name}
                                      inputId={'media-file-wrapper' + String(index) + type}
                                      onRemove={() => {
                                        form.setFieldValue(['factors', index, 'file'], null);
                                        const newFileList = fileList;
                                        newFileList[index] = false;
                                        setFileList(newFileList);
                                        form.setFieldsValue({});
                                        setIsValuesChanged(true);
                                      }}
                                      editable
                                    /> : '';
                                  }}
                                  disabled={!isEditable()}
                                >
                                  {!fileList[index] &&
                                    <Button icon={<IconUploadFile/>} size={'small'}>{t('managerTasks.upload')}</Button>
                                  }

                                </Upload>
                              }
                            </>
                          );
                        }}

                      </Form.Item>
                    </Flex>;
                  }
                }
                width={212}
              />
              <Column
                dataIndex={'status'}
                title={t('managerTasks.status')}
                render={
                  (value: any, record: DataType, index: number) => {
                    return (<Form.Item name={[index, 'status']}>
                      <Select
                        value={value}
                        style={{ width: 160 }}
                        options={statusesOptions}
                        disabled={!Roles.hasAccess('canSetTaskChannelsStatus')}
                      />
                    </Form.Item>);
                  }
                }
                fixed={'right'}
                width={170}
              />
              <Column
                dataIndex={'commentary'}
                title={t('managerTasks.comments')}
                render={
                  (value: any, record: DataType, index: number) => {
                    const item = form.getFieldValue(['factors', index, 'commentary']);
                    return <>
                      {item &&
                        <div className={'media-factors__comments'}
                             onClick={() => {
                               if (!isEditable()) {
                                 return;
                               }

                               if (Number(item.split('/').pop()) === selectedCommentaryId) {
                                 setSelectedCommentaryId(0);
                               } else {
                                 setSelectedCommentaryId(Number(item.split('/').pop()));
                               }
                             }}>
                          <IconComments/>
                        </div>
                      }
                    </>;
                  }
                }
                fixed={'right'}
                width={100}
              />

            </Table>;
          }}
        </Form.List>
      </Form>
    </Flex>
  );
}
